import axios from 'axios';
import { getAuthToken } from '../utils/authUtils'; //

const API_BASE_URL = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';


/**
 * Check if a patient email is unique
 * @param {string} email - The email to check
 * @param {string} [currentPatientId] - The current patient ID (for updates)
 * @returns {Promise<boolean>} True if email is unique, false otherwise
 */
export const isEmailUnique = async (email, currentPatientId = null) => {
  try {
    const token = await getAuthToken();
    const response = await axios.post(`${API_BASE_URL}/patients/check-email`, {
      email,
      currentPatientId
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data.isUnique;
  } catch (error) {
    console.error('Error checking email uniqueness:', error);
    throw error;
  }
};

/**
 * Get patient by ID
 * @param {string} patientId - The patient ID
 * @returns {Promise<Object>} Patient data
 */
export const getPatient = async (patientId) => {
  try {
    const token = await getAuthToken();
    const response = await axios.get(`${API_BASE_URL}/clinical/patients/${patientId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching patient:', error);
    throw error;
  }
};

/**
 * Create a new patient
 * @param {Object} patientData - The patient data
 * @returns {Promise<Object>} Created patient data
 */
export const createPatient = async (patientData) => {
  try {
    const token = await getAuthToken();
    
    // Check if facilityId is provided
    if (!patientData.facilityId) {
      throw new Error('facilityId is required to create a patient');
    }
    
    const response = await axios.post(`${API_BASE_URL}/clinical/facilities/${patientData.facilityId}/patients`, patientData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error creating patient:', error);
    throw error;
  }
};

/**
 * Update an existing patient
 * @param {string} patientId - The patient ID
 * @param {Object} updateData - The data to update
 * @returns {Promise<Object>} Updated patient data
 */
export const updatePatient = async (patientId, updateData) => {
  try {
    const token = await getAuthToken();
    const response = await axios.put(`${API_BASE_URL}/clinical/patients/${patientId}`, updateData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error updating patient:', error);
    throw error;
  }
};

/**
 * Delete a patient
 * @param {string} patientId - The patient ID
 * @returns {Promise<Object>} Deletion result
 */
export const deletePatient = async (patientId) => {
  try {
    const token = await getAuthToken();
    const response = await axios.delete(`${API_BASE_URL}/clinical/patients/${patientId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error deleting patient:', error);
    throw error;
  }
};

/**
 * Search patients
 * @param {Object} searchCriteria - The search criteria
 * @returns {Promise<Array>} Array of matching patients
 */
export const searchPatients = async (searchCriteria) => {
  try {
    const token = await getAuthToken();
    
    // Check if facilityId is provided
    if (!searchCriteria.facilityId) {
      throw new Error('facilityId is required to search patients');
    }
    
    // Use the getFacilityPatients endpoint with search parameter
    const queryParams = new URLSearchParams();
    if (searchCriteria.search) {
      queryParams.append('search', searchCriteria.search);
    }
    if (searchCriteria.limit) {
      queryParams.append('limit', searchCriteria.limit.toString());
    }
    if (searchCriteria.page) {
      queryParams.append('page', searchCriteria.page.toString());
    }

    const response = await axios.get(`${API_BASE_URL}/clinical/facilities/${searchCriteria.facilityId}/patients?${queryParams}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error searching patients:', error);
    throw error;
  }
};

/**
 * Get patients by facility
 * @param {string} facilityId - The facility ID
 * @param {Object} options - Query options
 * @returns {Promise<Array>} Array of patients in the facility
 */
export const getPatientsByFacility = async (facilityId, options = {}) => {
  try {
    const token = await getAuthToken();
    const queryParams = new URLSearchParams();
    if (options.status) {
      queryParams.append('status', options.status);
    }
    if (options.limit) {
      queryParams.append('limit', options.limit.toString());
    }

    const response = await axios.get(`${API_BASE_URL}/clinical/facilities/${facilityId}/patients?${queryParams}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    // Backend returns { facilityId, patients, total } format
    return response.data.patients || [];
  } catch (error) {
    console.error('Error fetching patients by facility:', error);
    throw error;
  }
};

/**
 * Get all patients for multiple facilities
 * @param {Array} facilityIds - Array of facility IDs
 * @param {Object} options - Query options
 * @returns {Promise<Array>} Array of patients
 */
export const getPatientsForFacilities = async (facilityIds, options = {}) => {
  try {
    const token = await getAuthToken();
    const response = await axios.post(`${API_BASE_URL}/clinical/patients/by-facilities`, {
      facilityIds,
      ...options
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching patients for facilities:', error);
    throw error;
  }
};

/**
 * Get patient documents
 * @param {string} patientId - The patient ID
 * @returns {Promise<Array>} Array of patient documents
 */
export const getPatientDocuments = async (patientId) => {
  try {
    const token = await getAuthToken();
    const response = await axios.get(`${API_BASE_URL}/clinical/patients/${patientId}/patient-documents`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching patient documents:', error);
    throw error;
  }
};

/**
 * Create patient document
 * @param {Object} documentData - The document data
 * @returns {Promise<Object>} Created document data
 */
export const createPatientDocument = async (documentData) => {
  try {
    const token = await getAuthToken();
    const { patientId, ...documentPayload } = documentData;
    
    if (!patientId) {
      throw new Error('Patient ID is required');
    }
    
    const response = await axios.post(`${API_BASE_URL}/clinical/patients/${patientId}/patient-documents`, documentPayload, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error creating patient document:', error);
    throw error;
  }
};

/**
 * Delete patient document
 * @param {string} documentId - The document ID
 * @returns {Promise<Object>} Deletion result
 */
export const deletePatientDocument = async (documentId) => {
  try {
    const token = await getAuthToken();
    const response = await axios.delete(`${API_BASE_URL}/clinical/clinical-documents/${documentId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error deleting patient document:', error);
    throw error;
  }
};

/**
 * Get patient procedure reports
 * @param {string} patientId - The patient ID
 * @returns {Promise<Array>} Array of procedure reports
 */
export const getPatientProcedureReports = async (patientId) => {
  try {
    const token = await getAuthToken();
    const response = await axios.get(`${API_BASE_URL}/clinical/patients/${patientId}/procedure-reports`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching patient procedure reports:', error);
    throw error;
  }
};

/**
 * Get procedure results
 * @param {string} procedureReportId - The procedure report ID
 * @returns {Promise<Array>} Array of procedure results
 */
export const getProcedureResults = async (procedureReportId) => {
  try {
    const token = await getAuthToken();
    const response = await axios.get(`${API_BASE_URL}/clinical/patients/procedure-reports/${procedureReportId}/results`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching procedure results:', error);
    throw error;
  }
};

/**
 * Get all patients
 * @param {Object} options - Query options
 * @param {string} options.facilityId - Filter by facility ID
 * @param {string} options.status - Filter by patient status
 * @param {number} options.limit - Limit number of results
 * @param {number} options.offset - Offset for pagination
 * @param {string} options.search - Search term for patient name or ID
 * @returns {Promise<Array>} Array of patients
 */
export const getAllPatients = async (options = {}) => {
  try {
    const token = await getAuthToken();
    const queryParams = new URLSearchParams();
    
    if (options.facilityId) {
      queryParams.append('facilityId', options.facilityId);
    }
    if (options.status) {
      queryParams.append('status', options.status);
    }
    if (options.limit) {
      queryParams.append('limit', options.limit.toString());
    }
    if (options.offset) {
      queryParams.append('offset', options.offset.toString());
    }
    if (options.search) {
      queryParams.append('search', options.search);
    }

    // Always require facilityId for security - users should only access patients in their facility
    if (!options.facilityId) {
      throw new Error('facilityId is required to fetch patients');
    }
    
    const response = await axios.get(`${API_BASE_URL}/clinical/facilities/${options.facilityId}/patients`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data.patients || [];
  } catch (error) {
    console.error('Error fetching all patients:', error);
    throw error;
  }
};

/**
 * Search for patients under 18 years old for linking purposes
 * @param {string} searchTerm - The search term (minimum 2 characters)
 * @param {number} limit - Maximum number of results (default 50)
 * @param {string} excludePatientId - Patient ID to exclude from results
 * @returns {Promise<Object>} Search results with child patients
 */
export const searchChildPatients = async (searchTerm, limit = 500, excludePatientId = null) => {
  try {
    const token = await getAuthToken();
    const params = new URLSearchParams({
      searchTerm,
      limit: limit.toString()
    });
    
    if (excludePatientId) {
      params.append('excludePatientId', excludePatientId);
    }

    const response = await axios.get(`${API_BASE_URL}/patients/search/children?${params}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error searching child patients:', error);
    throw error;
  }
};

/**
 * Link a child patient to an adult patient
 * @param {string} adultPatientId - The adult patient ID
 * @param {string} childPatientId - The child patient ID
 * @returns {Promise<Object>} Link result
 */
export const linkChildPatient = async (adultPatientId, childPatientId) => {
  try {
    const token = await getAuthToken();
    const response = await axios.post(`${API_BASE_URL}/patients/${adultPatientId}/link-child`, {
      childPatientId
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error linking child patient:', error);
    throw error;
  }
};

/**
 * Unlink a child patient from an adult patient
 * @param {string} adultPatientId - The adult patient ID
 * @param {string} childPatientId - The child patient ID
 * @returns {Promise<Object>} Unlink result
 */
export const unlinkChildPatient = async (adultPatientId, childPatientId) => {
  try {
    const token = await getAuthToken();
    const response = await axios.delete(`${API_BASE_URL}/patients/${adultPatientId}/unlink-child/${childPatientId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error unlinking child patient:', error);
    throw error;
  }
};

/**
 * Get all child patients linked to an adult patient
 * @param {string} adultPatientId - The adult patient ID
 * @returns {Promise<Object>} Linked children data
 */
export const getLinkedChildren = async (adultPatientId) => {
  try {
    const token = await getAuthToken();
    const response = await axios.get(`${API_BASE_URL}/patients/${adultPatientId}/linked-children`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error getting linked children:', error);
    throw error;
  }
};
